import { Check, Map } from "lucide-react";

/**
 * Premium coded "AI roadmap" mockup: the phased deliverable a strategy engagement
 * produces (Discover, Blueprint, Build, Rollout), prioritised by impact. Hero visual
 * for the AI Strategy page; shows the plan, not a generic AI illustration.
 */
const phases = [
  { n: "1", title: "Discover", note: "Goals & workflows", status: "done" },
  { n: "2", title: "Blueprint", note: "Phased roadmap", status: "active" },
  { n: "3", title: "Build", note: "Integrations & AI", status: "todo" },
  { n: "4", title: "Rollout", note: "Train & measure", status: "todo" },
] as const;

export const AIRoadmapMockup = () => {
  return (
    <div className="relative w-full max-w-[400px] mx-auto select-none">
      {/* soft brand glow */}
      <div className="absolute -inset-8 -z-10 bg-[radial-gradient(circle_at_70%_25%,rgba(124,58,237,0.20),transparent_60%),radial-gradient(circle_at_25%_85%,rgba(99,102,241,0.14),transparent_60%)] blur-2xl" />

      {/* floating badge */}
      <div className="absolute -top-3 right-4 z-10 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-violet-900/20">
        Phased &middot; ROI-scored
      </div>

      <div className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.06] bg-white shadow-2xl shadow-violet-900/10">
        {/* header */}
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-4">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-violet-100 text-violet-600">
            <Map className="h-4 w-4" />
          </div>
          <span className="text-sm font-bold text-slate-900">Your AI Roadmap</span>
        </div>

        {/* phases */}
        <div className="relative space-y-3 px-5 py-5">
          {/* vertical connector */}
          <div className="absolute left-[2.05rem] top-8 bottom-8 w-px bg-slate-200" />
          {phases.map((p) => (
            <div key={p.n} className="relative flex items-center gap-3.5">
              <div
                className={`relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold ${
                  p.status === "done"
                    ? "bg-violet-600 text-white"
                    : p.status === "active"
                    ? "bg-white text-violet-600 ring-2 ring-violet-500"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {p.status === "done" ? <Check className="h-4 w-4" /> : p.n}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-bold ${p.status === "todo" ? "text-slate-400" : "text-slate-900"}`}>
                  {p.title}
                </div>
                <div className="text-xs text-slate-400">{p.note}</div>
              </div>
              {p.status === "active" && (
                <span className="shrink-0 rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-600">
                  In progress
                </span>
              )}
              {p.status === "done" && (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-600">Done</span>
              )}
            </div>
          ))}
        </div>

        {/* footer: impact priority */}
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-5 py-3">
          <span className="text-xs font-medium text-slate-500">Prioritised by impact</span>
          <div className="flex items-center gap-1">
            <span className="h-1.5 w-6 rounded-full bg-violet-500" />
            <span className="h-1.5 w-4 rounded-full bg-violet-300" />
            <span className="h-1.5 w-3 rounded-full bg-slate-200" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRoadmapMockup;
